/**
 * Lecture Store - Zustand state management for lecture data
 * Validates: Requirements 3.7, 3.10, 11.1
 */

import { create } from 'zustand';
import type {
  LectureData,
  NoticeData,
  QuestionsData,
  ChatConfig,
} from '../types';
import {
  fetchLecture,
  fetchNotice,
  fetchQuestions,
  fetchChatConfig,
  fetchNgUsers,
  fetchNgWords,
  fetchAllData,
} from '../api/s3DataFetcher';

/** 問題ごとの回答分布データ */
export interface QuestionAggregation {
  choiceCounts: Record<string, number>;
  totalAnswers: number;
  correctCount: number;
}

/** ユーザーのランキング情報 */
export interface UserRankingInfo {
  rank: number;
  total_users: number;
  percentile: number;
  tier: 20 | 40 | null;
}

interface LectureStore {
  // State
  lectureData: LectureData | null;
  noticeData: NoticeData | null;
  questionsData: QuestionsData | null;
  chatConfig: ChatConfig | null;
  ngUsers: number[];
  ngWords: string[];
  isLoading: boolean;
  error: Error | null;
  /** 問題ごとの集計データ（questionId -> aggregation） */
  questionAggregations: Record<number, QuestionAggregation>;
  /** ユーザーのランキング情報 */
  userRanking: UserRankingInfo | null;
  /**
   * lectureData 読込前に受信した講義ステータス（受講生B-9対応）。
   * setLectureStatus は lectureData が null の間は反映できないため保留しておき、
   * fetchAllData / fetchLectureData の完了時に適用する。
   */
  pendingLectureStatus: 'ready' | 'running' | 'ended' | null;

  // Actions
  fetchLectureData: (tenantSlug: string, internalId: string, isPreview: boolean) => Promise<void>;
  fetchNoticeData: (tenantSlug: string, isPreview: boolean) => Promise<void>;
  fetchQuestionsData: (tenantSlug: string, lectureId: string) => Promise<void>;
  fetchChatConfig: (tenantSlug: string, lectureId: string) => Promise<void>;
  fetchNgUsers: (tenantSlug: string) => Promise<void>;
  fetchNgWords: (tenantSlug: string) => Promise<void>;
  fetchAllData: (tenantSlug: string, internalId: string, isPreview: boolean) => Promise<void>;
  /** リアルタイムイベントで問題を追加/更新する */
  upsertQuestion: (question: import('../types/test').Question) => void;
  /** 問題リストを一括置換する（/questions/ended の全問題データ反映用） */
  replaceQuestions: (questionsData: QuestionsData) => void;
  /** 問題のステータスのみ変更する */
  updateQuestionStatus: (questionId: number, status: import('../types/test').Question['status']) => void;
  /** 問題番号で問題を削除する（TestCancelled 用） */
  removeQuestionByNum: (questionNum: number) => void;
  /** 問題の集計データを保存する */
  setQuestionAggregation: (questionId: number, data: QuestionAggregation) => void;
  /** 講義ステータスをリアルタイムに更新する（WebSocket イベント用） */
  setLectureStatus: (status: 'ready' | 'running' | 'ended') => void;
  /** ユーザーのランキング情報を設定する */
  setUserRanking: (ranking: UserRankingInfo | null) => void;
  clearError: () => void;
  reset: () => void;
}

// 問題データの localStorage 永続化は LecturePage 側の effect で行う（STU-10対応）。
// 以前は本ストア内で meta.lecture.id を基に保存していたが、WebSocket 受信で構築される
// questionsData の meta.lecture.id は常に 0 のため一度も保存されない実質デッドコードだった。
// tenantSlug / lectureId を確実に持つ LecturePage で保存する方式に変更した。

const initialState = {
  lectureData: null,
  noticeData: null,
  questionsData: null,
  chatConfig: null,
  ngUsers: [],
  ngWords: [],
  isLoading: false,
  error: null,
  questionAggregations: {} as Record<number, QuestionAggregation>,
  userRanking: null as UserRankingInfo | null,
  pendingLectureStatus: null as 'ready' | 'running' | 'ended' | null,
};

/**
 * lectureData に講義ステータスを適用した新しい lectureData を返す（受講生B-9対応）。
 */
function applyLectureStatus(
  lectureData: LectureData,
  status: 'ready' | 'running' | 'ended',
): LectureData {
  const currentLive = lectureData.live || {
    title: '',
    description: '',
    playback_url: '',
    started_at: '',
    ended_at: '',
    is_live_published: false,
    status: 'ready' as const,
  };
  return {
    ...lectureData,
    live: { ...currentLive, status },
  };
}

export const useLectureStore = create<LectureStore>((set) => ({
  ...initialState,

  fetchLectureData: async (tenantSlug: string, internalId: string, isPreview: boolean) => {
    set({ isLoading: true, error: null });
    try {
      const data = await fetchLecture(tenantSlug, internalId, isPreview);
      set((state) => ({
        // 読込前に受信して保留していた講義ステータスがあれば適用する（受講生B-9対応）
        lectureData: state.pendingLectureStatus
          ? applyLectureStatus(data, state.pendingLectureStatus)
          : data,
        pendingLectureStatus: null,
        isLoading: false,
      }));
    } catch (error) {
      set({ error: error as Error, isLoading: false });
      console.error('Failed to fetch lecture data:', error);
    }
  },

  fetchNoticeData: async (tenantSlug: string, isPreview: boolean) => {
    set({ isLoading: true, error: null });
    try {
      const data = await fetchNotice(tenantSlug, isPreview);
      set({ noticeData: data, isLoading: false });
    } catch (error) {
      set({ error: error as Error, isLoading: false });
      console.error('Failed to fetch notice data:', error);
    }
  },

  fetchQuestionsData: async (tenantSlug: string, lectureId: string) => {
    set({ isLoading: true, error: null });
    try {
      const data = await fetchQuestions(tenantSlug, lectureId);
      set({ questionsData: data, isLoading: false });
    } catch (error) {
      set({ error: error as Error, isLoading: false });
      console.error('Failed to fetch questions data:', error);
    }
  },

  fetchChatConfig: async (tenantSlug: string, lectureId: string) => {
    set({ isLoading: true, error: null });
    try {
      const data = await fetchChatConfig(tenantSlug, lectureId);
      set({ chatConfig: data, isLoading: false });
    } catch (error) {
      set({ error: error as Error, isLoading: false });
      console.error('Failed to fetch chat config:', error);
    }
  },

  fetchNgUsers: async (tenantSlug: string) => {
    set({ isLoading: true, error: null });
    try {
      const data = await fetchNgUsers(tenantSlug);
      set({ ngUsers: data.ngUsers, isLoading: false });
    } catch (error) {
      set({ error: error as Error, isLoading: false });
      console.error('Failed to fetch NG users:', error);
    }
  },

  fetchNgWords: async (tenantSlug: string) => {
    set({ isLoading: true, error: null });
    try {
      const data = await fetchNgWords(tenantSlug);
      set({ ngWords: data.ngWords, isLoading: false });
    } catch (error) {
      set({ error: error as Error, isLoading: false });
      console.error('Failed to fetch NG words:', error);
    }
  },

  fetchAllData: async (tenantSlug: string, internalId: string, isPreview: boolean) => {
    set({ isLoading: true, error: null });
    try {
      const data = await fetchAllData(tenantSlug, internalId, isPreview);
      console.log('[Store][fetchAllData] S3データ取得完了。questionsData マージ開始');
      set((state) => {
        const storeQuestions = state.questionsData?.questions ?? [];
        // completedステータスも含める（解説終了済みの問題を保護）
        const activeRealtimeQuestions = storeQuestions.filter(
          (q) => q.status === 'answering' || q.status === 'tallying' || q.status === 'tallied' || q.status === 'completed',
        );
        console.log('[Store][fetchAllData] アクティブなリアルタイム問題:', activeRealtimeQuestions.length, 'S3の問題:', (data.questions.questions ?? []).length);
        console.log('[Store][fetchAllData] アクティブなリアルタイム問題のステータス:', activeRealtimeQuestions.map(q => ({ id: q.id, status: q.status })));

        const s3Questions = data.questions.questions ?? [];

        // S3 にある問題をベースに、同じ id のリアルタイム版があればそちらで置き換え
        const mergedQuestions = s3Questions.map((sq) => {
          const activeVersion = activeRealtimeQuestions.find((aq) => aq.id === sq.id);
          if (activeVersion) {
            console.log('[Store][fetchAllData] 問題', sq.id, 'をリアルタイム版で置き換え:', { s3Status: sq.status, realtimeStatus: activeVersion.status });
          }
          return activeVersion ?? sq;
        });

        // localStorage（s3Questions）に存在しないストア上の問題は、ステータスを問わず保持する。
        // /questions/ended で補完済みの未出題（not_started）問題をこの全置換で破棄すると、
        // 講義終了画面で出題済みの問題しか表示されなくなるため（講義終了画面集計問題対応）。
        for (const q of storeQuestions) {
          if (!mergedQuestions.some((sq) => sq.id === q.id)) {
            mergedQuestions.push(q);
          }
        }
        
        console.log('[Store][fetchAllData] マージ後の問題ステータス:', mergedQuestions.map(q => ({ id: q.id, status: q.status })));

        return {
          // 読込前に受信して保留していた講義ステータスがあれば適用する（受講生B-9対応）
          lectureData: state.pendingLectureStatus
            ? applyLectureStatus(data.lecture, state.pendingLectureStatus)
            : data.lecture,
          pendingLectureStatus: null,
          noticeData: data.notice,
          questionsData: { ...data.questions, questions: mergedQuestions },
          chatConfig: data.chatConfig,
          ngUsers: data.ngUsers.ngUsers,
          ngWords: data.ngWords.ngWords,
          isLoading: false,
        };
      });
    } catch (error) {
      set({ error: error as Error, isLoading: false });
      console.error('Failed to fetch all data:', error);
    }
  },

  upsertQuestion: (question) => {
    console.log('[Store][upsertQuestion] 呼び出し:', { id: question.id, status: question.status, sort_key: question.sort_key });
    set((state) => {
      let newQuestionsData;
      const current = state.questionsData;
      if (!current) {
        console.log('[Store][upsertQuestion] questionsData が null → 新規作成');
        newQuestionsData = {
          meta: { generated_at: '', version: '', tenant: { url_slug: '' }, lecture: { id: 0, name: '' } },
          questions: [question],
        };
      } else {
        const existingIndex = current.questions.findIndex((q) => q.id === question.id);
        const updatedQuestions =
          existingIndex >= 0
            ? current.questions.map((q, i) => (i === existingIndex ? question : q))
            : [...current.questions, question];
        console.log('[Store][upsertQuestion] 既存questions:', current.questions.length, '→ 更新後:', updatedQuestions.length, 'existingIndex:', existingIndex);
        console.log('[Store][upsertQuestion] 更新後のステータス一覧:', updatedQuestions.map(q => ({ id: q.id, status: q.status })));
        newQuestionsData = { ...current, questions: updatedQuestions };
      }

      return { questionsData: newQuestionsData };
    });
  },

  replaceQuestions: (questionsData) => {
    console.log('[Store][replaceQuestions] 問題リストを一括置換:', questionsData.questions.length, '問');
    set({ questionsData });
  },

  updateQuestionStatus: (questionId, status) => {
    console.log('[Store][updateQuestionStatus] 呼び出し:', { questionId, status });
    set((state) => {
      const current = state.questionsData;
      if (!current) {
        console.warn('[Store][updateQuestionStatus] questionsData が null');
        return {};
      }

      const newQuestionsData = {
        ...current,
        questions: current.questions.map((q) =>
          q.id === questionId ? { ...q, status } : q,
        ),
      };
      
      console.log('[Store][updateQuestionStatus] 更新後のステータス一覧:', newQuestionsData.questions.map(q => ({ id: q.id, status: q.status })));
      
      return { questionsData: newQuestionsData };
    });
  },

  removeQuestionByNum: (questionNum) => {
    set((state) => {
      const current = state.questionsData;
      if (!current) return {};

      return {
        questionsData: {
          ...current,
          questions: current.questions.map((q) =>
            q.sort_key === questionNum ? { ...q, status: 'not_started' as const } : q,
          ),
        },
      };
    });
  },

  setQuestionAggregation: (questionId, data) => {
    set((state) => ({
      questionAggregations: {
        ...state.questionAggregations,
        [questionId]: data,
      },
    }));
  },

  setLectureStatus: (status) => {
    console.log('[Store][setLectureStatus] 講義ステータス更新:', status);
    set((state) => {
      if (!state.lectureData) {
        // lectureData 読込前は捨てずに保留し、読込完了時に適用する（受講生B-9対応）
        console.warn('[Store][setLectureStatus] lectureData が未読込のため保留:', status);
        return { pendingLectureStatus: status };
      }

      console.log('[Store][setLectureStatus] 更新前:', state.lectureData.live?.status, '→ 更新後:', status);

      return {
        lectureData: applyLectureStatus(state.lectureData, status),
        pendingLectureStatus: null,
      };
    });
  },

  setUserRanking: (ranking) => {
    console.log('[Store][setUserRanking] ランキング情報更新:', ranking);
    set({ userRanking: ranking });
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set(initialState);
  },
}));
